/* MLS-SEARCH-FINDINGS-ENHANCEMENT-001 — frontend mirror of backend/services/mls_auto_findings.py.
   Generated findings live in `auto_findings`; processor notes stay in `notes` and are never touched here. */
import { UTILITY_TYPE_LABEL, utilityTypeOf, UTILITY_SOURCE_NOTE, isOffGridSource, stripSourceNote, sourceValidFor } from './mlsServiceTypes';

export const PERMIT_STATUS_VALUES = ['N/A', 'Open', 'Expired'];
export const UTILITY_PAYMENT_STATUS = ['Paid', 'Due', 'Delinquent', 'N/A']; // MLS-UTILITY-NA-001
export const UTILITY_ACCOUNT_STATUS = ['Active', 'Inactive', 'N/A'];

export const DEPENDENT_FIELDS = {
  code_violations: { flag: 'open_violations', positive: ['yes'], fields: ['case_number', 'type', 'amount_owed', 'good_thru'] },
  open_permits: { flag: 'open_expired_permit', positive: ['open', 'expired'], fields: ['permit_number', 'type', 'balance_owed', 'good_thru'] },
};
const UTILITY_KEYS = ['account_number', 'balance', 'good_thru', 'status', 'account_status', 'lienable'];
export const SECTION_FINDING_KEYS = {
  code_violations: ['open_violations', 'case_number', 'type', 'amount_owed', 'good_thru'],
  open_permits: ['open_expired_permit', 'permit_number', 'type', 'balance_owed', 'good_thru'],
  utility_accounts: UTILITY_KEYS,
  trash: UTILITY_KEYS,
};
export const NONE_FOUND_TEXT = {
  code_violations: 'No code violations found.',
  open_permits: 'No building permit records found.',
  utility_accounts: 'No utility records found.',
  trash: 'No utility records found.',
};

const s = (v) => String(v ?? '').trim();
const money = (v) => { const t = s(v); if (!t) return ''; const n = Number(t.replace(/[$,]/g, '')); return Number.isFinite(n) ? `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : t; };
const date = (v) => { const t = s(v); const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(t); return m ? `${m[2]}/${m[3]}/${m[1]}` : t; };
const join = (lead, parts) => { const p = parts.filter(Boolean); return p.length ? `${lead} — ${p.join('; ')}.` : `${lead}.`; };

/* `section` is the STORAGE key (code_violations | open_permits | utility_accounts | trash) */
export const generateFindings = (section, entry, noneFound) => {
  const e = entry || {};
  if (noneFound) return NONE_FOUND_TEXT[section] || '';
  if (section === 'code_violations') {
    const f = s(e.open_violations).toLowerCase();
    if (f === 'yes') return join('Open code violation on file', [
      s(e.case_number) && `Case # ${s(e.case_number)}`, s(e.type) && `Type: ${s(e.type)}`,
      s(e.amount_owed) && `Amount owed ${money(e.amount_owed)}`, s(e.good_thru) && `Good through ${date(e.good_thru)}`]);
    if (f === 'no' || f === 'none') return 'No open code violations reported.';
    if (f === 'n/a') return 'Code violation status not applicable.';
    return '';
  }
  if (section === 'open_permits') {
    const f = s(e.open_expired_permit).toLowerCase();
    if (f === 'open' || f === 'expired') return join(`${f.charAt(0).toUpperCase()}${f.slice(1)} building permit on file`, [
      s(e.permit_number) && `Permit # ${s(e.permit_number)}`, s(e.type) && `Type: ${s(e.type)}`,
      s(e.balance_owed) && `Balance owed ${money(e.balance_owed)}`, s(e.good_thru) && `Good through ${date(e.good_thru)}`]);
    if (f === 'n/a') return 'Permit status not applicable.';
    if (f === 'none') return 'No open or expired building permits reported.';
    return '';
  }
  if (section === 'utility_accounts' || section === 'trash') {
    // MLS-UTILITY-WELL-SEPTIC-001: a confirmed private well / septic tank IS the finding — no account data applies
    if (isOffGridSource(e.utility_source)) return UTILITY_SOURCE_NOTE[e.utility_source];
    const parts = [
      s(e.account_number) && `Acc # ${s(e.account_number)}`, s(e.balance) && `Balance ${money(e.balance)}`,
      s(e.good_thru) && `Good through ${date(e.good_thru)}`, s(e.status) && `Payment status: ${s(e.status)}`,
      s(e.account_status) && `Account status: ${s(e.account_status)}`, s(e.lienable) && `Lien-able: ${s(e.lienable)}`];
    return parts.some(Boolean) ? join(`${UTILITY_TYPE_LABEL[utilityTypeOf(e, section)] || 'Utility'} account`, parts) : '';
  }
  return '';
};

/* Is this finding field applicable right now? Dependent fields show only while the flag is positive
   (or while they still hold a legacy value). */
export const fieldApplicable = (section, entry, key) => {
  if (UTILITY_KEYS.includes(key) && isOffGridSource(entry?.utility_source)) return false; // well / septic: no public account fields
  const rule = DEPENDENT_FIELDS[section];
  if (!rule || !rule.fields.includes(key)) return true;
  return rule.positive.includes(s(entry?.[rule.flag]).toLowerCase()) || !!s(entry?.[key]);
};

/* Apply one finding change: flag leaving its positive state nulls the dependent fields (real nulls). */
export const applyFindingChange = (section, entry, key, value) => {
  const next = { ...entry, [key]: value };
  const rule = DEPENDENT_FIELDS[section];
  if (rule && key === rule.flag && s(value) && !rule.positive.includes(s(value).toLowerCase())) rule.fields.forEach((k) => { next[k] = null; });
  return next;
};

/* MLS-UTILITY-WELL-SEPTIC-001/003: Water Source / Sewer Type change. Well / septic nulls the public account findings;
   the well/septic sentence lives ONLY in Auto-Generated Findings — Additional Notes stay processor-only (an auto
   sentence left there by an earlier version is removed verbatim, everything else is preserved). A value that does not
   belong to the entry's utility type is dropped. */
export const applySourceChange = (entry, utype, value) => {
  const next = { ...entry };
  if (value && sourceValidFor(utype, value)) next.utility_source = value; else delete next.utility_source;
  if (isOffGridSource(next.utility_source)) UTILITY_KEYS.forEach((k) => { next[k] = null; });
  const notes = Object.values(UTILITY_SOURCE_NOTE).reduce((t, sentence) => stripSourceNote(t, sentence), String(entry?.notes || ''));
  if (notes !== String(entry?.notes || '')) next.notes = notes || null;
  return next;
};

/* None Found checked → finding values are cleared (entity, contact and manual notes are preserved). */
export const clearFindings = (section, entry) => {
  const next = { ...entry };
  (SECTION_FINDING_KEYS[section] || []).forEach((k) => { next[k] = null; });
  return next;
};
